import React, { useState, useEffect, useRef, useCallback } from 'react';
import GroupMembersModal from './GroupMembersModal';
import { BackIcon, UsersIcon, SendIcon, ClipboardIcon } from './icons';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { formatVisitDate } from '../utils/dateUtils';

const GroupChatView = ({ group, appointments, onBack, onSendMessage, onAddMembers, onRemoveMember, onRevokeInvite, onUpdateInviteName, onApproveMemberRequest, onDenyMemberRequest, onSelectAppointment, onMarkRead, onRefreshMessages }) => {
  const [newMessage, setNewMessage] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const keyboardHeight = useKeyboardHeight();
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const touchStartY = useRef(null);
  const pullDistance = useRef(0);
  const inputRef = useRef(null);

  // Auto-scroll to latest message on mount and when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [group.messages]);

  // Mark messages as read when chat opens
  useEffect(() => {
    if (onMarkRead && group.id) {
      onMarkRead(group.id);
    }
  }, [group.id, onMarkRead]);

  // Poll for new messages every 10 seconds while chat is open
  useEffect(() => {
    if (!onRefreshMessages) return;
    const interval = setInterval(() => {
      onRefreshMessages(group.id);
    }, 10000);
    // Also refresh immediately on mount
    onRefreshMessages(group.id);
    return () => clearInterval(interval);
  }, [group.id, onRefreshMessages]);

  const handleSend = () => {
    if (newMessage.trim()) {
      onSendMessage(group.id, newMessage);
      setNewMessage('');
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr) =>
    formatVisitDate(dateStr, { month: 'short', day: 'numeric' });

  const getMember = (id) => group.members.find(m => m.id === id || m.userId === id);

  const appMembers = group.members.filter(m => m.hasApp);
  const emailMembers = group.members.filter(m => !m.hasApp && m.email);
  const invitedCount = group.pendingInvites?.length || group.pendingInviteCount || 0;

  // Build member summary line
  const memberParts = [];
  if (appMembers.length > 0) memberParts.push(`${appMembers.length} on app`);
  if (emailMembers.length > 0) memberParts.push(`${emailMembers.length} via email`);
  if (invitedCount > 0) memberParts.push(`${invitedCount} invited`);

  // Get shared visits for this group
  const groupVisits = (group.sharedVisits || []).concat(
    appointments.filter(apt => apt.groupId === group.id)
  );
  // Deduplicate by id
  const visitMap = new Map();
  groupVisits.forEach(v => visitMap.set(v.id, v));
  const uniqueVisits = Array.from(visitMap.values());

  // Build a combined timeline of messages, shared visit cards, and member requests
  const buildTimeline = () => {
    const items = [];

    // Add messages
    (group.messages || []).forEach(msg => {
      items.push({
        type: 'message',
        data: msg,
        timestamp: msg.timestamp || msg.createdAt,
      });
    });

    // Add shared visit cards as system events
    uniqueVisits.forEach(visit => {
      const sharedAt = visit.shared_at || visit.sharedAt;
      if (sharedAt) {
        items.push({
          type: 'visit',
          data: visit,
          timestamp: sharedAt,
        });
      }
    });

    // Add pending member requests as timeline items
    (group.memberRequests || []).filter(r => r.status === 'pending').forEach(req => {
      items.push({
        type: 'member_request',
        data: req,
        timestamp: req.createdAt,
      });
    });

    // Sort by timestamp
    items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return items;
  };

  const timeline = buildTimeline();

  // ── Pull-to-refresh ─────────────────────────────────────────────────

  const handleTouchStart = useCallback((e) => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = null;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) {
      pullDistance.current = Math.min(delta, 100);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance.current > 60 && !isRefreshing) {
      setIsRefreshing(true);
      // Simulate refresh (reload messages from backend would go here)
      console.log('[ClariMe] Pull-to-refresh triggered for group:', group.id);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    }
    touchStartY.current = null;
    pullDistance.current = 0;
  }, [isRefreshing, group.id]);

  // ── Visit summary card (inline in chat) ─────────────────────────────

  const VisitCard = ({ visit }) => {
    const provider = visit.provider_name || visit.provider || 'Doctor';
    const specialty = visit.specialty || '';
    const date = visit.visit_date || visit.date;
    const headline = visit.summary?.headline || visit.summary?.familySummary?.substring(0, 80);

    return (
      <button
        onClick={() => onSelectAppointment && onSelectAppointment({
          id: visit.id,
          provider,
          specialty,
          date,
          summary: visit.summary,
          groupId: group.id,
        })}
        className="w-full bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100 rounded-xl p-3 text-left active:bg-teal-100 transition-colors"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <ClipboardIcon className="w-3.5 h-3.5 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-teal-800 truncate">
              {provider}{specialty ? ` - ${specialty}` : ''}
            </p>
            {date && (
              <p className="text-xs text-teal-600">{formatDate(date)}</p>
            )}
          </div>
          <svg className="w-4 h-4 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        {headline && (
          <p className="text-xs text-teal-700 leading-relaxed line-clamp-2 ml-9">
            {headline}
          </p>
        )}
      </button>
    );
  };

  // ── Member request approval card (inline in chat) ─────────────────
  const MemberRequestCard = ({ request }) => {
    const [processing, setProcessing] = useState(false);

    const handleApprove = async () => {
      if (!onApproveMemberRequest) return;
      setProcessing(true);
      try {
        await onApproveMemberRequest(group.id, request.id);
      } catch (err) {
        console.warn('[ClariMe] Approve failed:', err.message);
      } finally {
        setProcessing(false);
      }
    };

    const handleDeny = async () => {
      if (!onDenyMemberRequest) return;
      setProcessing(true);
      try {
        await onDenyMemberRequest(group.id, request.id);
      } catch (err) {
        console.warn('[ClariMe] Deny failed:', err.message);
      } finally {
        setProcessing(false);
      }
    };

    return (
      <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">
              {request.requesterName || 'A member'} wants to add
            </p>
            <p className="text-sm text-amber-700 font-semibold">
              {request.contactName || request.contactEmail}
            </p>
            {request.contactEmail && request.contactName && (
              <p className="text-xs text-amber-600">{request.contactEmail}</p>
            )}
          </div>
        </div>
        {group.isOwner ? (
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleDeny}
              disabled={processing}
              className="flex-1 py-2 bg-white border border-red-200 text-red-600 rounded-lg text-sm font-medium active:bg-red-50 disabled:opacity-50"
            >
              Deny
            </button>
            <button
              onClick={handleApprove}
              disabled={processing}
              className="flex-1 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium active:bg-teal-600 disabled:opacity-50"
            >
              {processing ? 'Processing...' : 'Approve'}
            </button>
          </div>
        ) : (
          <div className="mt-1">
            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
              Pending owner approval
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-x-0 top-0 bg-slate-50 flex flex-col" style={{ height: keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100dvh' }}>
      <div className="flex-shrink-0 bg-white border-b border-slate-100">
        <div className="px-4 pt-safe pb-3 flex items-center gap-3">
          <button onClick={onBack} className="p-2 -ml-2 rounded-full active:bg-slate-100 text-slate-500">
            <BackIcon />
          </button>
          <button className="flex-1 text-left active:opacity-70" onClick={() => setShowMembers(true)}>
            <h1 className="font-semibold text-slate-900">{group.name}</h1>
            <p className="text-xs text-teal-600">
              {memberParts.length > 0 ? memberParts.join(', ') : `${group.members?.length || 0} members`}
              <span className="ml-1">&rsaquo;</span>
            </p>
          </button>
          <button onClick={() => setShowMembers(true)} className="w-10 h-10 rounded-full active:bg-teal-100 bg-teal-50 flex items-center justify-center border border-teal-200">
            <UsersIcon className="w-6 h-6 text-teal-700" />
          </button>
        </div>
      </div>

      {showMembers && (
        <GroupMembersModal group={group} onClose={() => setShowMembers(false)} onAddMembers={onAddMembers} onRemoveMember={onRemoveMember} onRevokeInvite={onRevokeInvite} onUpdateInviteName={onUpdateInviteName} />
      )}

      {/* Pull-to-refresh indicator */}
      {isRefreshing && (
        <div className="flex-shrink-0 flex items-center justify-center py-2 bg-slate-50">
          <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-xs text-slate-500">Refreshing...</span>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {timeline.map((item, idx) => {
          if (item.type === 'member_request') {
            return (
              <div key={`req-${item.data.id}`} className="flex justify-center">
                <div className="w-full max-w-[85%]">
                  <MemberRequestCard request={item.data} />
                </div>
              </div>
            );
          }

          if (item.type === 'visit') {
            return (
              <div key={`visit-${item.data.id}`} className="flex justify-center">
                <div className="w-full max-w-[85%]">
                  <div className="flex justify-center mb-2">
                    <span className="bg-slate-100 text-slate-500 text-xs px-3 py-1 rounded-full">
                      Visit summary shared
                    </span>
                  </div>
                  <VisitCard visit={item.data} />
                </div>
              </div>
            );
          }

          const msg = item.data;
          const member = getMember(msg.senderId);
          const isMe = msg.senderId === 'u1' || msg.isMe;
          const isSystem = msg.senderId === 'system' || msg.source === 'system';

          if (isSystem) {
            return (
              <div key={msg.id} className="flex justify-center">
                <div className="bg-slate-100 text-slate-500 text-xs px-3 py-1.5 rounded-full">
                  {msg.text}
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${isMe ? 'order-2' : ''}`}>
                {!isMe && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-slate-600">{member?.name || msg.senderName || 'Unknown'}</span>
                    {msg.source === 'email' && (
                      <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">via email</span>
                    )}
                  </div>
                )}
                <div className={`rounded-2xl px-4 py-2.5 ${isMe ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white' : 'bg-white border border-slate-100 text-slate-800'}`}>
                  <p>{msg.text}</p>
                </div>
                <p className={`text-xs text-slate-400 mt-1 ${isMe ? 'text-right' : ''}`}>
                  {formatTime(msg.timestamp || msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex-shrink-0 bg-white border-t border-slate-100 p-4 pb-safe">
        <p className="text-xs text-slate-400 text-center mb-3">
          Family without the app will be notified by email
        </p>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 bg-slate-100 rounded-full outline-none focus:ring-2 ring-teal-500/20"
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${newMessage.trim() ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/20' : 'bg-slate-100 text-slate-400'}`}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupChatView;
